import { Injectable, inject } from '@angular/core';
import { Update } from '@ngrx/entity';
import { select, Store } from '@ngrx/store';
import { first } from 'rxjs/operators';

import { Device } from '@app/store/device.model';
import * as fromRoot from '@app/store/index';
import { mapParameterToValueChanges } from '@app/store/parameter.model';
import { SignalGeneratorType } from '@app/store/signal-generator.model';
import {
  updateTuningControllerParameter,
  updateTuningControllerParameters,
} from '@app/store/tuning-controller-parameter.actions';
import { TuningControllerParameter } from '@app/store/tuning-controller-parameter.model';
import {
  initDeviceTuningControllers,
  selectTuningControllerType,
  updateTuningController,
} from '@app/store/tuning-controller.actions';
import { TuningController, TuningControllerType } from '@app/store/tuning-controller.model';

@Injectable({
  providedIn: 'root',
})
export class TuningControllerService {
  private store$ = inject<Store<fromRoot.AppState>>(Store);

  selectedTuningControllerType$ = this.store$.pipe(select(fromRoot.selectSelectedTuningControllerType));

  initDeviceTuningControllers(device: Device) {
    this.store$.dispatch(initDeviceTuningControllers({ device }));
  }

  selectTuningControllerType(tuningControllerType: TuningControllerType) {
    this.store$.dispatch(selectTuningControllerType({ tuningControllerType }));
  }

  selectTuningControllerById(tuningControllerId: string) {
    return this.store$.pipe(select(fromRoot.createTuningControllerByIdSelector(tuningControllerId)));
  }

  selectTuningControllersByDeviceSerialNumber(deviceSerialNumber: string) {
    return this.store$.pipe(select(fromRoot.createTuningControllersByDeviceSerialNumberSelector(deviceSerialNumber)));
  }

  selectTuningControllerSignalGeneratorByType(tuningController: Partial<TuningController>, type: SignalGeneratorType) {
    return this.store$.pipe(
      select(fromRoot.createTuningControllerSignalGeneratorByTypeSelector(tuningController.id, type)),
    );
  }

  selectTuningControllerSignalGeneratorBySelectedType(tuningController: Partial<TuningController>) {
    return this.store$.pipe(
      select(fromRoot.createTuningControllerSignalGeneratorBySelectedTypeSelector(tuningController.id)),
    );
  }

  selectTuningControllerParameters(tuningController: Partial<TuningController>) {
    return this.store$.select(
      fromRoot.createTuningControllerParametersByTuningControllerIdSelector(tuningController.id),
    );
  }

  selectTuningControllerParameterById(tuningControllerParameterId: string) {
    return this.store$.pipe(select(fromRoot.createTuningControllerParameterByIdSelector(tuningControllerParameterId)));
  }

  updateTuningControllerParameterValues(values: { [key: string]: any }) {
    this.store$.pipe(select('tuningControllerParameters'), first()).subscribe((parameters) => {
      if (parameters.ids.length === 0) {
        console.warn('There are no tuning controller parameters in the store to update.', values);
        return;
      }

      const entries = Object.entries(values);

      const tuningControllerParameterUpdates: Update<TuningControllerParameter>[] = entries
        .map((entry) => {
          const [id, value] = entry;
          const parameter = parameters.entities[id];
          if (parameter) {
            const changes: Partial<TuningControllerParameter> = mapParameterToValueChanges(parameter, value);
            return { changes, id };
          }
        })
        .filter((update) => update !== undefined);

      if (tuningControllerParameterUpdates.length === 1) {
        const tuningControllerParameterUpdate = tuningControllerParameterUpdates[0];
        this.store$.dispatch(updateTuningControllerParameter({ update: tuningControllerParameterUpdate }));
      } else if (tuningControllerParameterUpdates.length > 1) {
        this.store$.dispatch(updateTuningControllerParameters({ updates: tuningControllerParameterUpdates }));
      } else {
        console.warn('There are no matching tuning controller parameters in the store to update.', values);
      }
    });
  }

  updateTuningController(tuningController: Partial<TuningController>) {
    this.store$.dispatch(
      updateTuningController({
        update: {
          changes: tuningController,
          id: tuningController.id,
        },
      }),
    );
  }
}
